import React from 'react';
import { formatDistance } from 'date-fns';
import { Session } from '../../types/session';

interface SessionListSidebarProps {
  sessions: Session[];
  selectedSessionId?: string;
  onSessionSelect: (session: Session) => void;
  onRefresh: () => void;
  onEndSession: (sessionId: string) => void;
  isLoading?: boolean;
  endingSessionId?: string | null;
}

export const SessionListSidebar: React.FC<SessionListSidebarProps> = ({
  sessions,
  selectedSessionId,
  onSessionSelect,
  onRefresh,
  onEndSession,
  isLoading = false,
  endingSessionId = null,
}) => {
  return (
    <div className="w-80 bg-white shadow-lg flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-lg font-bold text-gray-900">
          Active Sessions ({sessions.length})
        </h2>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <p>No active sessions</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {sessions.map((session) => {
              const isSelected = selectedSessionId === session.sessionId;
              const startedText = session.startedAt
                ? formatDistance(
                    new Date(session.startedAt),
                    new Date(),
                    { addSuffix: true }
                  )
                : 'Unknown';

              const lastPositionText = session.lastPositionAt
                ? formatDistance(new Date(session.lastPositionAt), new Date(), { addSuffix: true })
                : 'No positions';

              const isStale = session.lastPositionAt &&
                new Date().getTime() - new Date(session.lastPositionAt).getTime() > 5 * 60 * 1000;

              const isEnding = endingSessionId === session.sessionId;

              return (
                <li
                  key={session.sessionId}
                  className={`p-4 cursor-pointer transition ${
                    isSelected
                      ? 'bg-blue-50 border-l-4 border-blue-600'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onSessionSelect(session)}
                >
                  <div className="font-semibold text-gray-900">
                    {session.transportId} / {session.journeyId}
                  </div>
                  <div className="text-sm text-gray-600">
                    {session.licensePlate}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Started {startedText}
                  </div>
                  <div className={`text-xs mt-1 ${isStale ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    Last seen: {lastPositionText} {isStale && '⚠️'}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEndSession(session.sessionId);
                    }}
                    disabled={isEnding}
                    className="mt-2 w-full px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-xs font-medium transition"
                  >
                    {isEnding ? 'Ending...' : 'End Session'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Refresh Button */}
      <div className="border-t border-gray-200 p-4">
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition font-medium"
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};
